import { ManagedSelect } from "components/orders/Fields/Select/ManagedSelect";
import { ManagedToggle } from "components/orders/Fields/Toggle/ManagedToggle";
import React, { useEffect, useState } from "react";
import type { DeckFormType } from "./DeckSchema";
import "./Deck.scss";
import type { AnchorsDeckValidity } from "../AnchorsForm/AnchorsForm";
import {
  deckingTypeOptions,
  hardwareTypeOptions,
  paddingTypeOptions,
} from "./DeckConstants";
import type { FeatureMeasurements } from "models/Project";
import { useT } from "i18n";

interface DeckProps {
  title: string;
  showHardwareDropdown: boolean;
  image?: string;
  notes?: string;
  setDeck: (deck: DeckFormType) => void;
  validFields: AnchorsDeckValidity;
  deck: DeckFormType;
  length: number;
  archived?: boolean;
  featureName: string | null | undefined;
  measurement?: FeatureMeasurements;
}

// Labels moved to use translations

const Deck = ({
  title,
  showHardwareDropdown,
  image,
  notes,
  setDeck,
  validFields,
  deck,
  length,
  archived,
  featureName,
  measurement,
}: DeckProps) => {
  const t = useT();
  const translateDeckOptionLabel = (value: string, label: string): string => {
    const translationMap: Record<string, string> = {
      Concrete: "component.deckConstants.concrete",
      "Pavers with a concrete base": "component.deckConstants.paversConcrete",
      "Pavers with a sand base": "component.deckConstants.paversSand",
      "Grass or Dirt": "component.deckConstants.grassDirt",
      "Wood/Composite": "component.deckConstants.woodComposite",
      "Standard Brass Anchors": "component.deckConstants.standardBrassAnchors",
      '2" Long Brass Anchors': "component.deckConstants.twoInchBrassAnchors",
      "Deck Tubes": "component.deckConstants.deckTubes",
      "Lawn Tubes": "component.deckConstants.lawnTubes",
      "Wood Deck Anchors": "component.deckConstants.woodDeckAnchors",
      "Sewn On": "component.deckConstants.sewnOn",
      Replaceable: "component.deckConstants.replaceable",
      'Loose (2" X 20")': "component.deckConstants.loose",
    };
    const translationKey = translationMap[value];
    return translationKey ? t(translationKey, label) : label;
  };

  const translateDeckOptions = (
    options: Array<{ label: string; value: string }>,
  ): Array<{ label: string; value: string }> => {
    return options.map((option) => ({
      ...option,
      label: translateDeckOptionLabel(option.value, option.label),
    }));
  };

  if (typeof deck?.decking === undefined) {
    throw new Error("Decks are required for deck component to render!");
  }

  const isRDM =
    measurement &&
    measurement.deckingProperties &&
    (measurement.deckingProperties[0]?.numPoints > 0 ||
      measurement?.deckingProperties[1]?.numPoints > 0 ||
      measurement?.deckingProperties[2]?.numPoints > 0);

  // Field states
  const [deckingType, setDeckingType] = useState<string | undefined>(
    deck?.decking ?? undefined,
  );
  const [hardwareOption, setHardwareOption] = useState<string | undefined>(
    deck?.hardware ?? undefined,
  );
  const [includeBrassCollars, setIncludeBrassCollars] = useState(
    deck?.collars ?? undefined,
  );
  const [isRough, setIsRough] = useState(deck?.padding ?? false);
  const [paddingType, setPaddingType] = useState<string | undefined>(
    deck?.paddingType ?? undefined,
  );

  useEffect(() => {
    if (!showHardwareDropdown && !isRDM) {
      setHardwareOption(undefined);
      setIncludeBrassCollars(undefined);
    }
  });

  useEffect(() => {
    setDeck({
      decking: deckingType,
      hardware: hardwareOption,
      padding: isRough,
      collars: includeBrassCollars,
      paddingType: paddingType,
      length: length,
      featureName: featureName,
      notes: notes,
      rdm: isRDM,
    } as DeckFormType);
  }, [deckingType, hardwareOption, isRough, paddingType, includeBrassCollars, isRDM]);

  useEffect(() => {
    // Clear paddingType if isRough toggle switches to "off"
    if (!isRough) setPaddingType(undefined);
  }, [isRough]);

  return (
    <div className={"deck"}>
      <div className={"deck__title"}>{title}</div>
      {image ? (
        <img className={"deck__image"} src={image} />
      ) : (
        <div>{t("component.common.noImageFound", "No image found.")}</div>
      )}
      {notes ?? <div>{notes}</div>}
      {isRDM && (
        <span className={"rdmContainer"}>
          <i className={"rdmIcon exclamation triangle icon"} />
          <div className={"rdmWarning"}>
            {t("component.deck.rdmDeck", "This is an RDM deck")}
          </div>
        </span>
      )}
      <ManagedSelect
        label={t("component.deck.deckingType", "Decking Type")}
        isValid={validFields.decking}
        value={deckingType}
        onChange={setDeckingType}
        options={translateDeckOptions(deckingTypeOptions)}
        name={"deck-type"}
        disabled={archived}
      />
      {(showHardwareDropdown || isRDM) && (
        <ManagedSelect
          label={t("component.deck.hardware", "Hardware")}
          isValid={validFields.hardware}
          value={hardwareOption}
          onChange={setHardwareOption}
          name={"hardware-type"}
          options={
            deckingType
              ? translateDeckOptions(
                  hardwareTypeOptions[deckingType as keyof typeof hardwareTypeOptions],
                )
              : []
          }
          disabled={archived}
        />
      )}
      {!!hardwareOption && (showHardwareDropdown || isRDM) && (
        <ManagedToggle
          label={t("component.deck.includeBrassCollars", "Include Brass Collars")}
          name={"collars"}
          isValid={validFields.collars}
          onChange={setIncludeBrassCollars}
          checked={includeBrassCollars ?? false}
          disabled={archived}
        />
      )}
      <ManagedToggle
        label={t(
          "component.deck.padding",
          "Is the coping rough / would you like additional perimeter padding",
        )}
        name={"padding"}
        isValid={validFields.padding}
        onChange={setIsRough}
        checked={isRough}
        disabled={archived}
      />
      {isRough && (
        <ManagedSelect
          label={t("component.deck.paddingType", "Padding Type")}
          isValid={validFields.paddingType}
          value={paddingType}
          onChange={setPaddingType}
          options={translateDeckOptions(paddingTypeOptions)}
          name={"is-rough"}
          disabled={archived}
        />
      )}
    </div>
  );
};

export default Deck;
